/**
 * Launch velocity computation for arrow + bow combinations.
 *
 * Industry IBO derate formula (the standard way bow manufacturers spec):
 *
 *   v_actual = iboFps
 *             + 10  × (drawLength_in - 30)               // ~10 fps per inch
 *             - 1.5 × ((arrowGrains - 350) / 3)          // vs the fixed 350gr IBO arrow
 *             + (drawWeight_lb - 70) × 2
 *
 * Calibration note (2026-06): two factors were wrong and over-predicted speed
 * (a 460gr arrow on an 80lb / 28.5" / 336-IBO rig read 328fps; Sal chronos 294).
 *   1. The IBO reference arrow is a FIXED 350gr (the weight the IBO speed is
 *      measured with) — NOT 5gpp × drawWeight, which inflated heavy-draw rigs.
 *   2. The draw-length term was -1.5 fps/in with the wrong sign (it made SHORT
 *      draws read faster). The published standard is ~10 fps per inch, longer
 *      draw = faster.
 *
 * Sources: IBO/ATA spec method, widely published in archery ballistics.
 */

/** IBO standard arrow mass — speed is rated with a fixed 350gr arrow. */
const IBO_REFERENCE_MASS_GR = 350;

import type { Build } from './types';

export function computeLaunchVelocityFps(build: Build): number {
  /* If user has a chronograph reading, use that — it's more accurate than
   * the IBO derate for any single rig because it accounts for cam choice,
   * brace height variation, string + cable wear, and ambient temperature. */
  if (build.measuredVelocityFps != null && build.measuredVelocityFps > 0) {
    return build.measuredVelocityFps;
  }

  /* Draw length: ~10 fps per inch from the 30" IBO standard (longer = faster). */
  const drawLengthAdj = 10 * (build.bowDrawLengthIn - 30);
  /* Arrow weight: ~1 fps slower per 3 grains over the FIXED 350gr IBO
   * reference arrow (the published derate). A steeper 1.5 fps/3gr was
   * over-penalizing heavy arrows — a 550gr shaft fell to 230fps (real ~260). */
  const massAdj = -1.0 * ((build.arrowGrains - IBO_REFERENCE_MASS_GR) / 3);
  const weightAdj = (build.bowDrawWeightLb - 70) * 2;

  return build.bowIboFps + drawLengthAdj + massAdj + weightAdj;
}
